import type { IntlShape, MessageDescriptor } from 'react-intl';
import { Option } from 'effect';

import { encodeMarker } from '@/lib/i18nOverrides/markers';
import { isDeferredOverrideApplicable, type Messages } from '@/lib/i18nOverrides/mergeOverrides';
import type { MarkerRegistry, PrimitiveValue } from '@/lib/i18nOverrides/registry';

/*
 * Both wrappers patch only `formatMessage` / `$t`. That is enough: <FormattedMessage>, useIntl() and injectIntl all
 * format through the intl object in context (react-intl 6), and nothing in the app creates its own intl.
 */
type FormatMessage = IntlShape['formatMessage'];
type Values = Parameters<FormatMessage>[1];
type Opts = Parameters<FormatMessage>[2];

const withFormatMessage = (
  intl: IntlShape,
  wrap: (original: FormatMessage, descriptor: MessageDescriptor, values: Values, opts: Opts) => unknown,
): IntlShape => {
  const original = intl.formatMessage;
  const formatMessage = ((descriptor: MessageDescriptor, values: Values, opts: Opts) =>
    wrap(original, descriptor, values, opts)) as FormatMessage;
  return { ...intl, formatMessage, $t: formatMessage };
};

/**
 * Applies overrides of keys the catalog lacks, checked against the call site's defaultMessage — the only base such a
 * key has. The check is cached per (key, defaultMessage, value).
 */
export const withDeferredOverrides = (intl: IntlShape, deferred: Messages): IntlShape => {
  if (Object.keys(deferred).length === 0) return intl;
  const verdicts = new Map<string, boolean>();
  const applicable = (id: string, defaultMessage: string | undefined, value: string) => {
    const cacheKey = `${id}\u0000${defaultMessage ?? ''}\u0000${value}`;
    const cached = verdicts.get(cacheKey);
    if (cached !== undefined) return cached;
    const verdict = isDeferredOverrideApplicable(defaultMessage, value);
    verdicts.set(cacheKey, verdict);
    return verdict;
  };
  return withFormatMessage(intl, (original, descriptor, values, opts) => {
    const id = descriptor.id;
    const value = id === undefined ? undefined : deferred[id];
    const defaultMessage = typeof descriptor.defaultMessage === 'string' ? descriptor.defaultMessage : undefined;
    return value !== undefined && id !== undefined && applicable(id, defaultMessage, value)
      ? original({ ...descriptor, defaultMessage: value }, values, opts)
      : original(descriptor, values, opts);
  });
};

const isPrimitive = (v: unknown): v is PrimitiveValue =>
  v === null || v === undefined || ['string', 'number', 'boolean'].includes(typeof v);

const primitiveSnapshot = (values: Values) =>
  values === undefined
    ? undefined
    : Object.fromEntries(
        Object.entries(values).filter((entry): entry is [string, PrimitiveValue] => isPrimitive(entry[1])),
      );

/** Type-to-confirm guards compare the typed text with the formatted keyword; a marker would make them never match. */
export const isNeverMarked = (id: string): boolean => id.endsWith('.keyword');

const isEmptyOutput = (out: unknown) => out === '' || (Array.isArray(out) && out.length === 0);

/** Edit mode: appends an index marker to every formatted catalog text (string, or rich-text array as a last chunk). */
export const withMarkers = (intl: IntlShape, registry: MarkerRegistry): IntlShape =>
  withFormatMessage(intl, (original, descriptor, values, opts) => {
    const out: unknown = original(descriptor, values, opts);
    const id = descriptor.id;
    // An empty catalog value renders as the id or the default text in react-intl; there is nothing to click anyway.
    if (id === undefined || isNeverMarked(id) || isEmptyOutput(out) || intl.messages[id] === '') return out;
    if (typeof out !== 'string' && !Array.isArray(out)) return out;
    const defaultMessage = typeof descriptor.defaultMessage === 'string' ? descriptor.defaultMessage : undefined;
    return Option.match(registry.register({ id, defaultMessage, values: primitiveSnapshot(values) }), {
      onNone: () => out,
      onSome: (index) => (typeof out === 'string' ? out + encodeMarker(index) : [...out, encodeMarker(index)]),
    });
  });
